import { useState } from "react"
import { useParams } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { apiTenant, ApiError } from "../lib/api"

type RunStatus = "draft" | "computed" | "approved" | "paid" | "reversed"

interface Run {
  id: string
  periodStart: string
  periodEnd: string
  payDate: string | null
  frequency: string
  ruleSetId: string
  engineVersion: string
  status: RunStatus
  notes: string | null
  totalGross: string
  totalEpfEmployee: string
  totalEpfEmployer: string
  totalEtfEmployer: string
  totalApit: string
  totalNet: string
  totalEmployees: number
  createdAt: string
}

interface Payslip {
  id: string
  employeeNoSnap: string
  fullNameSnap: string
  basicEarned: string
  grossPay: string
  epfEmployee: string
  apitAmount: string
  netPay: string
  contentHash: string
}

interface RunDetail extends Run {
  payslips: Payslip[]
}

interface PayslipDetail {
  payslip: Payslip & {
    bodyJson: Record<string, unknown>
    lineItems: Array<{
      id: string
      category: "earning" | "deduction" | "statutory" | "informational"
      code: string
      description: string
      quantity: string | null
      rate: string | null
      amount: string
    }>
    taxLineItems: Array<{
      id: string
      tableId: string
      bracketFrom: string
      bracketTo: string | null
      rate: string
      taxableAmount: string
      taxAmount: string
    }>
    run: Run
  }
  verification: { stored: string; recomputed: string; verified: boolean }
}

const STATUS_PILL: Record<RunStatus, string> = {
  draft: "bg-slate-200 text-slate-700",
  computed: "bg-blue-100 text-blue-700",
  approved: "bg-amber-100 text-amber-700",
  paid: "bg-emerald-100 text-emerald-700",
  reversed: "bg-red-100 text-red-700",
}

function fmtMoney(s: string | number): string {
  const n = typeof s === "string" ? Number(s) : s
  return n.toLocaleString("en-LK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function startOfMonth(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`
}
function endOfMonth(d: Date): string {
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0)
  return `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}`
}

export function PayrollPage() {
  const { slug = "" } = useParams<{ slug: string }>()
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [openRunId, setOpenRunId] = useState<string | null>(null)
  const [openPayslipId, setOpenPayslipId] = useState<string | null>(null)

  const runs = useQuery({
    queryKey: ["payroll-runs", slug],
    enabled: !!slug,
    queryFn: () => apiTenant<{ runs: Run[] }>(slug, "/payroll/runs"),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Payroll</h1>
          <p className="text-xs text-slate-500 mt-1">Tenant <span className="font-mono">{slug}</span></p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="px-3 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded"
        >
          + New run
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded">
        {runs.isLoading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
        {runs.error && <div className="p-6 text-sm text-red-600">{(runs.error as Error).message}</div>}
        {runs.data && runs.data.runs.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-400">
            No payroll runs yet. Click <strong className="text-slate-600">+ New run</strong> to start one.
          </div>
        )}
        {runs.data && runs.data.runs.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
              <tr>
                <th className="text-left font-medium px-4 py-2">Period</th>
                <th className="text-left font-medium px-4 py-2">Status</th>
                <th className="text-right font-medium px-4 py-2">Employees</th>
                <th className="text-right font-medium px-4 py-2">Gross (LKR)</th>
                <th className="text-right font-medium px-4 py-2">Net (LKR)</th>
                <th className="text-left font-medium px-4 py-2">Rule set</th>
                <th className="text-right font-medium px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {runs.data.runs.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                  <td className="px-4 py-2">
                    {r.periodStart.slice(0, 10)} → {r.periodEnd.slice(0, 10)}
                    <div className="text-xs text-slate-400">{r.frequency}</div>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`inline-block px-2 py-0.5 text-xs rounded ${STATUS_PILL[r.status]}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{r.totalEmployees}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmtMoney(r.totalGross)}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmtMoney(r.totalNet)}</td>
                  <td className="px-4 py-2 text-xs text-slate-500 font-mono">{r.ruleSetId}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => setOpenRunId(r.id)}
                      className="text-xs text-brand-600 hover:text-brand-800"
                    >
                      Open →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <CreateRunModal
          slug={slug}
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            qc.invalidateQueries({ queryKey: ["payroll-runs", slug] })
            setShowCreate(false)
            setOpenRunId(id)
          }}
        />
      )}

      {openRunId && (
        <RunDetailDrawer
          slug={slug}
          runId={openRunId}
          onClose={() => setOpenRunId(null)}
          onOpenPayslip={(id) => setOpenPayslipId(id)}
        />
      )}

      {openPayslipId && (
        <PayslipDetailModal
          slug={slug}
          payslipId={openPayslipId}
          onClose={() => setOpenPayslipId(null)}
        />
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Create run modal
// ────────────────────────────────────────────────────────────

function CreateRunModal(props: { slug: string; onClose: () => void; onCreated: (id: string) => void }) {
  const today = new Date()
  // Default: previous calendar month (most common monthly run timing).
  const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1)
  const [periodStart, setPeriodStart] = useState(startOfMonth(lastMonth))
  const [periodEnd, setPeriodEnd] = useState(endOfMonth(lastMonth))
  const [frequency, setFrequency] = useState("monthly")
  const [error, setError] = useState("")

  const mutation = useMutation({
    mutationFn: () =>
      apiTenant<{ run: Run }>(props.slug, "/payroll/runs", {
        method: "POST",
        body: JSON.stringify({ periodStart, periodEnd, frequency }),
      }),
    onSuccess: (res) => props.onCreated(res.run.id),
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.message === "duplicate_run_for_period") {
          setError("A run already exists for that period — open it from the list instead.")
        } else if (err.message === "no_active_rule_set") {
          setError("No statutory rule set covers that period. Contact a platform admin.")
        } else {
          setError(err.message)
        }
      } else {
        setError(err instanceof Error ? err.message : "Failed to create run")
      }
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError("")
    if (!periodStart || !periodEnd) {
      setError("Period start and end are required.")
      return
    }
    if (periodEnd < periodStart) {
      setError("Period end must be on or after period start.")
      return
    }
    mutation.mutate()
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <form onSubmit={handleSubmit} className="bg-white rounded-lg w-full max-w-md shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="font-semibold">New payroll run</div>
          <button type="button" onClick={props.onClose} className="text-slate-400 hover:text-slate-700">
            ×
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Period start *">
              <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="form-input" />
            </Field>
            <Field label="Period end *">
              <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="form-input" />
            </Field>
          </div>
          <Field label="Frequency">
            <select value={frequency} onChange={(e) => setFrequency(e.target.value)} className="form-input">
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
              <option value="fortnightly">Fortnightly</option>
              <option value="adhoc">Ad-hoc</option>
            </select>
          </Field>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={props.onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white text-sm font-medium rounded"
          >
            {mutation.isPending ? "Creating…" : "Create draft"}
          </button>
        </div>
      </form>
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Run detail drawer — compute / approve / mark-paid + payslips list
// ────────────────────────────────────────────────────────────

function RunDetailDrawer(props: {
  slug: string
  runId: string
  onClose: () => void
  onOpenPayslip: (id: string) => void
}) {
  const qc = useQueryClient()
  const [actionError, setActionError] = useState("")

  const detail = useQuery({
    queryKey: ["payroll-run", props.slug, props.runId],
    queryFn: () => apiTenant<{ run: RunDetail }>(props.slug, `/payroll/runs/${props.runId}`),
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["payroll-run", props.slug, props.runId] })
    qc.invalidateQueries({ queryKey: ["payroll-runs", props.slug] })
  }

  const compute = useMutation({
    mutationFn: () => apiTenant<{ totals: Record<string, number> }>(props.slug, `/payroll/runs/${props.runId}/compute`, { method: "POST" }),
    onSuccess: invalidate,
    onError: (err) => setActionError(err instanceof Error ? err.message : "Compute failed"),
  })
  const approve = useMutation({
    mutationFn: () => apiTenant(props.slug, `/payroll/runs/${props.runId}/approve`, { method: "POST" }),
    onSuccess: invalidate,
    onError: (err) => setActionError(err instanceof Error ? err.message : "Approve failed"),
  })
  const markPaid = useMutation({
    mutationFn: () => apiTenant(props.slug, `/payroll/runs/${props.runId}/mark-paid`, { method: "POST" }),
    onSuccess: invalidate,
    onError: (err) => setActionError(err instanceof Error ? err.message : "Mark paid failed"),
  })
  const reverse = useMutation({
    mutationFn: () => apiTenant(props.slug, `/payroll/runs/${props.runId}/reverse`, { method: "POST" }),
    onSuccess: () => {
      invalidate()
      // Close the drawer so the user lands back on the runs list and
      // sees both the original (now "reversed") and the new reversal.
      props.onClose()
    },
    onError: (err) => setActionError(err instanceof Error ? err.message : "Reverse failed"),
  })

  function runAction(fn: () => void, confirmMsg?: string) {
    setActionError("")
    if (confirmMsg && !window.confirm(confirmMsg)) return
    fn()
  }

  const run = detail.data?.run

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end">
      <div className="bg-white w-full max-w-3xl h-full overflow-y-auto shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="font-semibold">Payroll run</div>
            {run && <div className="text-xs text-slate-500">{run.periodStart.slice(0, 10)} → {run.periodEnd.slice(0, 10)} · {run.frequency}</div>}
          </div>
          <button type="button" onClick={props.onClose} className="text-slate-400 hover:text-slate-700">×</button>
        </div>

        {detail.isLoading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
        {detail.error && <div className="p-6 text-sm text-red-600">{(detail.error as Error).message}</div>}

        {run && (
          <div className="p-5 space-y-5">
            {/* Status + actions */}
            <div className="flex items-center gap-3">
              <span className={`inline-block px-2 py-0.5 text-xs rounded ${STATUS_PILL[run.status]}`}>{run.status}</span>
              <div className="text-xs text-slate-500">{run.ruleSetId} · engine v{run.engineVersion}</div>
              <div className="flex-1" />
              {(run.status === "draft" || run.status === "computed") && (
                <button
                  onClick={() => runAction(() => compute.mutate(), run.status === "computed" ? "Recompute will replace existing payslips. Continue?" : undefined)}
                  disabled={compute.isPending}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white text-xs font-medium rounded"
                >
                  {compute.isPending ? "Computing…" : run.status === "computed" ? "Recompute" : "Compute"}
                </button>
              )}
              {run.status === "computed" && (
                <button
                  onClick={() => runAction(() => approve.mutate(), `Approve payroll for ${run.totalEmployees} employee(s) totalling LKR ${fmtMoney(run.totalNet)}? This locks the run from edits.`)}
                  disabled={approve.isPending}
                  className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 text-white text-xs font-medium rounded"
                >
                  {approve.isPending ? "Approving…" : "Approve"}
                </button>
              )}
              {run.status === "approved" && (
                <button
                  onClick={() => runAction(() => markPaid.mutate(), "Mark this run as paid? Confirm only after the bank transfer has cleared.")}
                  disabled={markPaid.isPending}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-xs font-medium rounded"
                >
                  {markPaid.isPending ? "Saving…" : "Mark paid"}
                </button>
              )}
              {(run.status === "approved" || run.status === "paid") && (
                <button
                  onClick={() => runAction(() => reverse.mutate(), `Reverse this run? This creates a new opposing run and marks the original as REVERSED. Use for genuine errors only — auditors will see both records.`)}
                  disabled={reverse.isPending}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white text-xs font-medium rounded"
                >
                  {reverse.isPending ? "Reversing…" : "Reverse"}
                </button>
              )}
            </div>

            {actionError && <div className="text-sm text-red-600">{actionError}</div>}

            {/* Totals */}
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Employees" value={String(run.totalEmployees)} />
              <Stat label="Gross" value={fmtMoney(run.totalGross)} />
              <Stat label="Net" value={fmtMoney(run.totalNet)} accent />
              <Stat label="EPF (employee)" value={fmtMoney(run.totalEpfEmployee)} />
              <Stat label="EPF (employer)" value={fmtMoney(run.totalEpfEmployer)} />
              <Stat label="ETF (employer)" value={fmtMoney(run.totalEtfEmployer)} />
              <Stat label="APIT" value={fmtMoney(run.totalApit)} />
            </div>

            {/* Payslip preview */}
            <div>
              <div className="text-sm font-medium text-slate-700 mb-2">Payslips ({run.payslips.length})</div>
              {run.payslips.length === 0 ? (
                <div className="text-sm text-slate-400 bg-slate-50 border border-dashed border-slate-200 rounded p-4">
                  Run hasn't been computed yet. Click Compute to generate payslips.
                </div>
              ) : (
                <div className="border border-slate-200 rounded">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">#</th>
                        <th className="text-left px-3 py-2 font-medium">Name</th>
                        <th className="text-right px-3 py-2 font-medium">Gross</th>
                        <th className="text-right px-3 py-2 font-medium">EPF 8%</th>
                        <th className="text-right px-3 py-2 font-medium">APIT</th>
                        <th className="text-right px-3 py-2 font-medium">Net</th>
                        <th className="text-right px-3 py-2 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {run.payslips.map((p) => (
                        <tr key={p.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                          <td className="px-3 py-1.5 font-mono text-xs">{p.employeeNoSnap}</td>
                          <td className="px-3 py-1.5">{p.fullNameSnap}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{fmtMoney(p.grossPay)}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{fmtMoney(p.epfEmployee)}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{fmtMoney(p.apitAmount)}</td>
                          <td className="px-3 py-1.5 text-right font-mono font-semibold">{fmtMoney(p.netPay)}</td>
                          <td className="px-3 py-1.5 text-right">
                            <button
                              onClick={() => props.onOpenPayslip(p.id)}
                              className="text-xs text-brand-600 hover:text-brand-800"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Payslip detail modal
// ────────────────────────────────────────────────────────────

function PayslipDetailModal(props: { slug: string; payslipId: string; onClose: () => void }) {
  const detail = useQuery({
    queryKey: ["payslip", props.slug, props.payslipId],
    queryFn: () => apiTenant<PayslipDetail>(props.slug, `/payslips/${props.payslipId}`),
  })

  function handleDownload() {
    // Browser handles auth header via fetch + blob download (the SPA's
    // Authorization header isn't sent on plain anchor clicks).
    const token = localStorage.getItem("people.token") ?? ""
    const url = (import.meta.env.PROD ? "/people/api" : "/api") + `/v1/t/${props.slug}/payslips/${props.payslipId}/pdf`
    fetch(url, { headers: { authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.blob()
      })
      .then((blob) => {
        const link = document.createElement("a")
        link.href = URL.createObjectURL(blob)
        link.download = `payslip-${props.payslipId}.pdf`
        document.body.appendChild(link)
        link.click()
        link.remove()
      })
      .catch((err) => alert(`Download failed: ${err.message}`))
  }

  const ps = detail.data?.payslip
  const verification = detail.data?.verification

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="font-semibold">Payslip detail</div>
            {ps && <div className="text-xs text-slate-500 font-mono">{ps.employeeNoSnap} · {ps.fullNameSnap}</div>}
          </div>
          <div className="flex items-center gap-2">
            {ps && (
              <button onClick={handleDownload} className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs font-medium rounded">
                Download PDF
              </button>
            )}
            <button type="button" onClick={props.onClose} className="text-slate-400 hover:text-slate-700">×</button>
          </div>
        </div>

        {detail.isLoading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
        {detail.error && <div className="p-6 text-sm text-red-600">{(detail.error as Error).message}</div>}

        {ps && verification && (
          <div className="p-5 space-y-4">
            {/* Verification banner */}
            <div className={`text-xs rounded p-2 ${verification.verified ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
              {verification.verified
                ? `✓ Hash verified — content matches stored signature`
                : `✗ Hash mismatch — payslip body has been tampered with. Stored ${verification.stored.slice(0, 12)}… vs recomputed ${verification.recomputed.slice(0, 12)}…`}
            </div>

            {/* Earnings */}
            <LineItemsBlock title="Earnings" items={ps.lineItems.filter((l) => l.category === "earning")} showQtyRate />
            <LineItemsBlock title="Deductions" items={ps.lineItems.filter((l) => l.category === "deduction")} />
            <LineItemsBlock title="Statutory deductions" items={ps.lineItems.filter((l) => l.category === "statutory")} />

            {/* Net summary */}
            <div className="bg-slate-50 border border-slate-200 rounded p-3 grid grid-cols-2 gap-2 text-sm">
              <div>Gross</div>
              <div className="text-right font-mono">{fmtMoney(ps.grossPay)}</div>
              <div className="font-semibold">Net pay</div>
              <div className="text-right font-mono font-semibold text-base">{fmtMoney(ps.netPay)}</div>
            </div>

            <LineItemsBlock title="Employer contributions (informational)" items={ps.lineItems.filter((l) => l.category === "informational")} />

            {/* APIT bracket detail */}
            {ps.taxLineItems.length > 0 && (
              <div>
                <div className="text-sm font-medium text-slate-700 mb-2">APIT bracket detail</div>
                <table className="w-full text-xs border border-slate-200 rounded">
                  <thead className="text-xs text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-2 py-1">Table</th>
                      <th className="text-right px-2 py-1">From</th>
                      <th className="text-right px-2 py-1">To</th>
                      <th className="text-right px-2 py-1">Rate</th>
                      <th className="text-right px-2 py-1">Taxable</th>
                      <th className="text-right px-2 py-1">Tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ps.taxLineItems.map((t) => (
                      <tr key={t.id} className="border-b border-slate-100 last:border-b-0">
                        <td className="px-2 py-1 font-mono">{t.tableId}</td>
                        <td className="px-2 py-1 text-right font-mono">{fmtMoney(t.bracketFrom)}</td>
                        <td className="px-2 py-1 text-right font-mono">{t.bracketTo == null ? "∞" : fmtMoney(t.bracketTo)}</td>
                        <td className="px-2 py-1 text-right">{(Number(t.rate) * 100).toFixed(0)}%</td>
                        <td className="px-2 py-1 text-right font-mono">{fmtMoney(t.taxableAmount)}</td>
                        <td className="px-2 py-1 text-right font-mono">{fmtMoney(t.taxAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="text-[10px] text-slate-400 font-mono break-all">
              SHA-256: {ps.contentHash}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Small UI helpers
// ────────────────────────────────────────────────────────────

function Stat(props: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="bg-white border border-slate-200 rounded p-2.5">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{props.label}</div>
      <div className={`text-base font-mono ${props.accent ? "font-semibold text-emerald-700" : "text-slate-900"}`}>
        {props.value}
      </div>
    </div>
  )
}

function LineItemsBlock(props: {
  title: string
  items: Array<{ id: string; code: string; description: string; quantity: string | null; rate: string | null; amount: string }>
  showQtyRate?: boolean
}) {
  if (props.items.length === 0) return null
  return (
    <div>
      <div className="text-sm font-medium text-slate-700 mb-2">{props.title}</div>
      <table className="w-full text-xs border border-slate-200 rounded">
        <thead className="text-slate-500 border-b border-slate-200">
          <tr>
            <th className="text-left px-2 py-1 font-medium">Description</th>
            {props.showQtyRate && (
              <>
                <th className="text-right px-2 py-1 font-medium">Qty</th>
                <th className="text-right px-2 py-1 font-medium">Rate</th>
              </>
            )}
            <th className="text-right px-2 py-1 font-medium">Amount</th>
          </tr>
        </thead>
        <tbody>
          {props.items.map((i) => (
            <tr key={i.id} className="border-b border-slate-100 last:border-b-0">
              <td className="px-2 py-1">{i.description}</td>
              {props.showQtyRate && (
                <>
                  <td className="px-2 py-1 text-right font-mono">{i.quantity ?? ""}</td>
                  <td className="px-2 py-1 text-right font-mono">{i.rate ? fmtMoney(i.rate) : ""}</td>
                </>
              )}
              <td className="px-2 py-1 text-right font-mono">{fmtMoney(i.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Field(props: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1">{props.label}</div>
      {props.children}
    </label>
  )
}
